import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Plus, Trash2, Edit2, Check, Home, Building2, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { customerAPI } from '../../services/api';

const SavedAddressesTab = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    is_default: false,
    special_instructions: ''
  });

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      const res = await customerAPI.getAddresses();
      setAddresses(res.data || []);
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await customerAPI.updateAddress(editingId, formData);
        toast.success('Address updated');
      } else {
        await customerAPI.addAddress(formData);
        toast.success('Address added');
      }
      fetchAddresses();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save address');
    }
  };

  const handleEdit = (address) => {
    setFormData({
      label: address.label,
      address: address.address,
      city: address.city,
      state: address.state,
      zip_code: address.zip_code,
      is_default: address.is_default,
      special_instructions: address.special_instructions || ''
    });
    setEditingId(address.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await customerAPI.deleteAddress(id);
      setAddresses(addresses.filter(a => a.id !== id));
      toast.success('Address deleted');
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  const resetForm = () => {
    setFormData({
      label: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      is_default: false,
      special_instructions: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getLabelIcon = (label) => {
    const lower = label.toLowerCase();
    if (lower.includes('home')) return <Home className="w-5 h-5" />;
    if (lower.includes('office') || lower.includes('work')) return <Building2 className="w-5 h-5" />;
    return <MapPin className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="card p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading addresses...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <MapPin className="w-6 h-6 text-[var(--primary)]" />
            Saved Addresses
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Manage your service locations for faster booking
          </p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="btn-primary" data-testid="add-address-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Address
          </Button>
        )}
      </div>

      {/* Add/Edit Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="card p-6 mb-6 border-2 border-[var(--primary)]"
          >
            <h3 className="font-bold mb-4">{editingId ? 'Edit Address' : 'Add New Address'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Label *</label>
                  <Input
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    placeholder="e.g., Home, Parent's House, Office"
                    required
                    data-testid="address-label"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    className="w-4 h-4 rounded border-[var(--border-primary)]"
                  />
                  <label htmlFor="is_default" className="text-sm">Set as default address</label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Street Address *</label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main Street"
                  required
                  data-testid="address-street"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">City *</label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="City"
                    required
                    data-testid="address-city"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">State *</label>
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="State"
                    required
                    data-testid="address-state"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">ZIP Code *</label>
                  <Input
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    placeholder="12345"
                    required
                    data-testid="address-zip"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Special Instructions</label>
                <Textarea
                  value={formData.special_instructions}
                  onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                  placeholder="Gate code, parking info, etc."
                  rows={2}
                  data-testid="address-instructions"
                />
              </div>

              <div className="flex gap-3">
                <Button type="submit" className="btn-primary" data-testid="save-address-btn">
                  <Check className="w-4 h-4 mr-2" />
                  {editingId ? 'Update' : 'Save'} Address
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Addresses List */}
      {addresses.length === 0 && !showForm ? (
        <div className="card p-12 text-center">
          <MapPin className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
          <h3 className="text-lg font-bold mb-2">No Saved Addresses</h3>
          <p className="text-[var(--text-secondary)] mb-4">
            Add your frequently used addresses for quicker booking.
          </p>
          <Button onClick={() => setShowForm(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Address
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <AnimatePresence>
            {addresses.map((addr) => (
              <motion.div
                key={addr.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`card p-5 ${addr.is_default ? 'border-2 border-[var(--primary)] bg-[var(--accent)]' : ''}`}
                data-testid={`address-${addr.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      addr.is_default ? 'bg-[var(--primary)] text-white' : 'bg-[var(--accent)] text-[var(--primary)]'
                    }`}>
                      {getLabelIcon(addr.label)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold">{addr.label}</h3>
                        {addr.is_default && (
                          <span className="text-xs bg-[var(--primary)] text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Star className="w-3 h-3" /> Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[var(--text-secondary)] mt-1">
                        {addr.address}
                      </p>
                      <p className="text-sm text-[var(--text-muted)]">
                        {addr.city}, {addr.state} {addr.zip_code}
                      </p>
                      {addr.special_instructions && (
                        <p className="text-xs text-[var(--text-muted)] mt-2 italic">
                          Note: {addr.special_instructions}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-4 pt-4 border-t border-[var(--border-primary)]">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEdit(addr)}
                    data-testid={`edit-address-${addr.id}`}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDelete(addr.id)}
                    className="text-[var(--error)] hover:text-red-700 hover:border-red-300"
                    data-testid={`delete-address-${addr.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default SavedAddressesTab;
